/**
 * @file WorkerCronLogsWidget.js
 * @version 2.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-05-29
 * @modified 2026-09-14
 *
 * Admin-only logger widget, now a two-panel tool.
 *
 * ALERTS (default) — SystemLogAlerts: everything across the system's logs
 * that looks like it needs action, grouped and markable as actioned. It leads
 * because the previous single-panel design relied on an admin scrolling a
 * reverse-chronological list to notice a problem, which is exactly how things
 * get missed.
 *
 * LOG — SystemLogViewer: the full log, filterable by date, date range,
 * source and kind. Previously this widget read public.sync_history only; the
 * viewer reads the unified feed covering every table the system logs to.
 *
 * SPEND — ClaudeSpendPanel: what the Claude API has cost this month, last
 * month and per day, by feature, and whether the account is refusing calls.
 * Added after the API account ran out of credit with nothing in the system to
 * say so; its failures also raise alerts in the ALERTS view.
 *
 * All three panels are shared components rather than local markup: the same
 * alerting panel is rendered by the status-bar "Issues Logged" modal in
 * Dashboard.js, so the two can't drift.
 */

import React, { useState, useCallback } from 'react';
import SystemLogAlerts from '../components/SystemLogAlerts';
import SystemLogViewer from '../components/SystemLogViewer';
import ClaudeSpendPanel from '../components/ClaudeSpendPanel';
import usePersistedState from '../hooks/usePersistedState';
import './WorkerCronLogsWidget.css';

function WorkerCronLogsWidget() {
  const [view, setView] = usePersistedState('workerCronLogs.view', 'alerts');
  const [alertCount, setAlertCount] = useState(null);

  // Identity-stable so SystemLogAlerts' fetch callback doesn't change every
  // render (it lives in that component's useCallback deps).
  const handleCount = useCallback((n) => setAlertCount(n), []);

  return (
    <div className="dashboard-card worker-cron-logs-card">
      <div className="terminal-titlebar">
        <div className="terminal-brand" aria-hidden="true">
          <span className="material-symbols-rounded terminal-brand-icon">terminal</span>
        </div>
        <div className="terminal-title">
          {{ alerts: 'system-alerts', log: 'system-log', spend: 'claude-spend' }[view] || 'system-log'}
        </div>
        <div className="terminal-controls">
          <button
            type="button"
            className={`terminal-btn terminal-btn-wide ${view === 'alerts' ? 'terminal-btn-active' : ''}`}
            onClick={() => setView('alerts')}
            title="Alerting panel — items needing action"
            aria-pressed={view === 'alerts'}
          >
            <span className="material-symbols-rounded">notification_important</span>
            <span className="terminal-btn-text">Alerts</span>
            {alertCount > 0 && <span className="terminal-btn-badge">{alertCount}</span>}
          </button>
          <button
            type="button"
            className={`terminal-btn terminal-btn-wide ${view === 'log' ? 'terminal-btn-active' : ''}`}
            onClick={() => setView('log')}
            title="Full searchable log"
            aria-pressed={view === 'log'}
          >
            <span className="material-symbols-rounded">receipt_long</span>
            <span className="terminal-btn-text">Log</span>
          </button>
          <button
            type="button"
            className={`terminal-btn terminal-btn-wide ${view === 'spend' ? 'terminal-btn-active' : ''}`}
            onClick={() => setView('spend')}
            title="Claude API spend and account status"
            aria-pressed={view === 'spend'}
          >
            <span className="material-symbols-rounded">payments</span>
            <span className="terminal-btn-text">Spend</span>
          </button>
        </div>
      </div>

      <div className="terminal-panel">
        {view === 'alerts' && <SystemLogAlerts variant="terminal" onCountChange={handleCount} />}
        {view === 'log' && <SystemLogViewer />}
        {view === 'spend' && <ClaudeSpendPanel />}
      </div>
    </div>
  );
}

export default WorkerCronLogsWidget;
